import { setLoadMultiplier, weightedVolumeKg } from "./setVolume.js";
import type { DropsetVariant } from "./dropsets.js";

// Single source of truth for "the total of one logged set", counting the
// top round AND every dropset/myorep chain step EXACTLY ONCE. A plain
// (non-chain) set is just a one-round list, so live totals (mobile
// totalsMath) and derived totals (shared sessionResult) route every set
// through the same function — the per-set SUBTOTAL in the Excel sense:
//   rounds → set subtotal → entry/member footer → workout footer
// Each level aggregates the subtotals below it, never the leaves again,
// so no round is ever double-counted. e1RM / PR stay the caller's
// concern (top round only); this owns volume + reps.

// A single round of a set, already parsed to canonical kg. Index 0 is the
// "top" round (the working/activation set); the rest are chain steps.
// NOTE: `weightKg` is canonical-kg BY CONVENTION for the volume/analytics
// callers. The math here is unit-agnostic (pure multiply), so the mobile
// timeline's display-unit Σ chip deliberately feeds display-unit magnitudes
// through the same helper (timelineRows.ts dropsetDisplayRounds) — fine as
// long as this function stays kg-blind (no rounding/plate-snap on weightKg).
//   weighted   → `weightKg` is the bar/stack load (null = blank input)
//   bodyweight → `extraKg` is the signed added/assisted load
//                (null/0 = pure bodyweight)
export type DropsetRound =
  | {
      kind: "weighted";
      reps: number;
      weightKg: number | null;
      loadedSides?: 1 | 2;
    }
  | {
      kind: "bodyweight";
      reps: number;
      extraKg: number | null;
      // Unilateral bodyweight (pistol, single-leg): one side logged, both
      // performed → ×2. Same read-default (1) as the weighted round.
      loadedSides?: 1 | 2;
    };

export type DropsetTotalsContext = {
  /** Profile bodyweight (kg). 0/absent → bodyweight rounds contribute 0,
   *  matching sessionResult's back-compat rule. */
  bodyweightKg?: number;
  /** Exercise's effective-load percentage on bodyweight sets (default
   *  100 = full bodyweight). */
  bodyweightLoadPct?: number;
};

// Resolve a chain step's weight under the myorep "same load" convention:
// a myorep step left blank repeats the top set's load (the clusters are
// the same weight by definition); a classical step left blank contributes
// no load — the weight genuinely dropped and the user just hasn't typed a
// number, so we don't invent one. The top round (index 0) never inherits:
// it IS the reference every other round reads from.
export const resolveRoundWeightKg = (
  variant: DropsetVariant,
  isTop: boolean,
  weightKg: number | null,
  topWeightKg: number | null,
): number | null => {
  if (weightKg !== null) return weightKg;
  if (isTop) return null;
  return variant === "myorep" ? topWeightKg : null;
};

// Volume (kg) + total reps for ONE set. `rounds[0]` is the top round; the
// remainder are the chain. `variant` only affects how a blank step weight
// is interpreted (see resolveRoundWeightKg); pass any value for a plain
// one-round set since the top round never inherits.
export const dropsetSetTotals = (
  variant: DropsetVariant,
  rounds: readonly DropsetRound[],
  ctx: DropsetTotalsContext = {},
): { reps: number; volumeKg: number } => {
  const bw = ctx.bodyweightKg ?? 0;
  const pct = ctx.bodyweightLoadPct ?? 100;
  const top = rounds[0];
  const topWeightKg = top && top.kind === "weighted" ? top.weightKg : null;
  let reps = 0;
  let volumeKg = 0;
  for (let i = 0; i < rounds.length; i++) {
    const r = rounds[i]!;
    if (!Number.isFinite(r.reps) || r.reps <= 0) continue;
    reps += r.reps;
    if (r.kind === "weighted") {
      const w = resolveRoundWeightKg(variant, i === 0, r.weightKg, topWeightKg);
      if (w === null || w <= 0) continue;
      volumeKg += weightedVolumeKg(w, r.reps, r.loadedSides);
    } else {
      if (bw <= 0 || pct <= 0) continue;
      const perRep = bw * (pct / 100) + (r.extraKg ?? 0);
      if (perRep > 0)
        volumeKg += perRep * r.reps * setLoadMultiplier(r.loadedSides);
    }
  }
  return { reps, volumeKg };
};
